/**
 * Contexto NPS — gerencia exibição do widget de NPS e view atual.
 *
 * Triggers são disparados por componentes filhos via triggerNPS().
 * A lógica de cooldown é delegada ao backend (/api/v1/nps/can-survey).
 * Um flag de sessão (useRef) evita múltiplas chamadas para o mesmo evento.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  ReactNode
} from 'react'
import { useUIConfigContext } from './UIConfigContext'
import NPSWidget from '../features/nps/NPSWidget'
import { checkNpsSurvey, submitNpsResponse } from '../services/api'

export type NpsEvent =
  | 'ANALYSIS_COMPLETE'
  | 'EXPORT'
  | 'COMPARISON'

interface NPSContextType {
  /** Tenta disparar o widget NPS para um evento específico */
  triggerNPS: (event: NpsEvent, fileId?: string) => void
  /** Sincroniza a view atual (chamado pelo App.tsx) */
  setCurrentView: (view: string) => void
  /** View atual — usada por FeedbackButton ao enviar feedback */
  currentView: string
}

const NPSContext = createContext<NPSContextType | null>(null)

export function useNPS(): NPSContextType {
  const ctx = useContext(NPSContext)
  if (!ctx) throw new Error('useNPS deve ser usado dentro de NPSProvider')
  return ctx
}

interface NPSProviderProps {
  children: ReactNode
}

export function NPSProvider({ children }: NPSProviderProps) {
  const { uiConfig } = useUIConfigContext()

  const [showWidget, setShowWidget] = useState(false)
  const [currentView, setCurrentView] = useState('files')

  // Guarda o evento pendente para enviar com a resposta
  const pendingRef = useRef<{ event: NpsEvent; fileId?: string } | null>(null)

  // Evita disparar o mesmo evento mais de uma vez por sessão
  const triggeredRef = useRef<Set<string>>(new Set())

  // Evita disparos simultâneos enquanto já está verificando
  const checkingRef = useRef(false)

  const triggerNPS = useCallback(
    async (event: NpsEvent, fileId?: string) => {
      const npsConfig = uiConfig?.npsConfig

      // Verificações de configuração
      if (!npsConfig?.npsEnabled) return
      if (
        npsConfig.triggerEvents?.length &&
        !npsConfig.triggerEvents.includes(event)
      ) return

      // Flag de sessão: cada evento+arquivo só dispara uma vez
      const sessionKey = fileId ? `${event}-${fileId}` : event
      if (triggeredRef.current.has(sessionKey)) return
      if (checkingRef.current) return

      triggeredRef.current.add(sessionKey)
      checkingRef.current = true

      try {
        const { canSurvey } = await checkNpsSurvey()
        if (!canSurvey) return

        pendingRef.current = { event, fileId }

        const delayMs = (npsConfig.triggerDelaySeconds ?? 3) * 1000
        setTimeout(() => setShowWidget(true), delayMs)
      } catch {
        // Falha silenciosa: não interrompe o fluxo do usuário
        triggeredRef.current.delete(sessionKey)
      } finally {
        checkingRef.current = false
      }
    },
    [uiConfig]
  )

  const handleClose = useCallback(() => {
    setShowWidget(false)
    pendingRef.current = null
  }, [])

  const handleSubmit = useCallback(
    async (score: number, feedback: string) => {
      const pending = pendingRef.current
      try {
        await submitNpsResponse({
          score,
          feedback: feedback.trim() || undefined,
          triggerEvent: pending?.event ?? 'UNKNOWN',
          fileId: pending?.fileId,
          currentView
        })
      } catch {
        // Falha silenciosa
      } finally {
        handleClose()
      }
    },
    [currentView, handleClose]
  )

  return (
    <NPSContext.Provider value={{ triggerNPS, setCurrentView, currentView }}>
      {children}

      {showWidget && (
        <NPSWidget onClose={handleClose} onSubmit={handleSubmit} />
      )}
    </NPSContext.Provider>
  )
}

export default NPSContext
